import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import { cn } from "@/lib/utils";
import {
  SquaresFour, ListChecks, ClipboardText, Users, Broadcast, Warning,
  ChartLine, ChartBar, Bell, Gear, Kanban, UserCircle, CaretLeft, CaretRight
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", key: "dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/my-queue", key: "my_queue", label: "My Queue", icon: ListChecks },
  { to: "/queue", key: "queue_board", label: "Queue Board", icon: Kanban },
  { to: "/cases", key: "cases", label: "Cases", icon: ClipboardText },
  { to: "/allocation", key: "allocation", label: "Allocation Engine", icon: Broadcast },
  { to: "/workforce", key: "workforce", label: "Workforce", icon: Users },
  { to: "/exceptions", key: "exceptions", label: "Exceptions", icon: Warning },
  { to: "/analytics", key: "analytics", label: "Analytics", icon: ChartLine },
  { to: "/reports", key: "reports", label: "Reports", icon: ChartBar },
  { to: "/notifications", key: "notifications", label: "Notifications", icon: Bell },
  { to: "/admin", key: "admin", label: "Administration", icon: Gear },
];

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user } = useAuth();
  const { can } = usePermissions();

  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        "sticky top-0 h-screen border-r border-black/20 dark:border-white/10 flex flex-col shrink-0 z-30",
        "bg-[hsl(202,61%,10%)] text-[hsl(158,30%,96%)]",
        collapsed ? "w-[70px]" : "w-[240px]",
        "transition-[width] duration-200"
      )}
    >
      <div className="h-16 flex items-center px-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <img src="/aptia-logo.png" alt="Aptia" className={collapsed ? "h-8 w-auto" : "h-9 w-auto"} />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 no-scrollbar" data-testid="sidebar-nav">
        {NAV.filter(n => can(n.key)).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`nav-${item.to.replace("/", "")}`}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-4 py-2.5 mx-2 rounded-md text-sm transition-colors",
              "text-[hsl(220,8%,72%)] hover:bg-white/5 hover:text-white",
              isActive && "bg-primary/15 text-white border-l-2 border-[hsl(var(--sidebar-active))]"
            )}
          >
            <item.icon size={18} weight="regular" />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="p-2 border-t border-white/10 space-y-1">
        <NavLink
          to="/profile"
          data-testid="nav-profile"
          className={({isActive}) => cn(
            "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors text-[hsl(220,8%,72%)] hover:bg-white/5 hover:text-white",
            isActive && "bg-primary/15 text-white"
          )}
        >
          <UserCircle size={20} />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium truncate text-white">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[hsl(220,8%,55%)]">{user?.role}</div>
            </div>
          )}
        </NavLink>
        <button
          data-testid="sidebar-collapse-btn"
          onClick={() => setCollapsed(v => !v)}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-xs text-[hsl(220,8%,60%)] hover:bg-white/5 hover:text-white transition-colors"
        >
          {collapsed ? <CaretRight size={16} /> : <><CaretLeft size={16} /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  );
}
